import React, { useState, useRef, createContext, useContext } from 'react';
import clsx from 'clsx';

// Context for tab state management
interface TabsContextValue {
  activeTab: string;
  setActiveTab: (id: string) => void;
  variant: 'default' | 'pills' | 'underline';
  size: 'sm' | 'md' | 'lg';
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext() {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('Tab components must be used within a Tabs component');
  }
  return context;
}

// Types
export interface TabItem {
  id: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
  badge?: React.ReactNode;
}

export interface TabsProps {
  children: React.ReactNode;
  defaultTab?: string;
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  variant?: 'default' | 'pills' | 'underline';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  fullWidth?: boolean;
}

export interface TabListProps {
  children: React.ReactNode;
  className?: string;
  'aria-label'?: string;
}

export interface TabProps {
  id: string;
  children: React.ReactNode;
  disabled?: boolean;
  icon?: React.ReactNode;
  badge?: React.ReactNode;
  className?: string;
}

export interface TabPanelProps {
  id: string;
  children: React.ReactNode;
  className?: string;
}

// Variant styles
const variantStyles = {
  default: {
    list: 'border-b border-gray-200 dark:border-gray-700',
    tab: {
      base: 'border-b-2 border-transparent',
      active: 'border-primary-500 text-primary-600 dark:text-primary-400',
      inactive: 'text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300',
    },
  },
  pills: {
    list: 'bg-gray-100 dark:bg-gray-800 p-1 rounded-lg',
    tab: {
      base: 'rounded-md',
      active: 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm',
      inactive: 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white',
    },
  },
  underline: {
    list: '',
    tab: {
      base: 'border-b-2 border-transparent -mb-px',
      active: 'border-primary-500 text-primary-600 dark:text-primary-400',
      inactive: 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300',
    },
  },
};

const sizeStyles = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-base',
};

// Main Tabs container
export function Tabs({
  children,
  defaultTab,
  activeTab: controlledActiveTab,
  onTabChange,
  variant = 'default',
  size = 'md',
  className,
  fullWidth = false,
}: TabsProps) {
  const [internalActiveTab, setInternalActiveTab] = useState(defaultTab || '');
  const isControlled = controlledActiveTab !== undefined;
  const activeTab = isControlled ? controlledActiveTab : internalActiveTab;

  const setActiveTab = (id: string) => {
    if (!isControlled) {
      setInternalActiveTab(id);
    }
    onTabChange?.(id);
  };

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab, variant, size }}>
      <div className={clsx('w-full', className)} data-fullwidth={fullWidth}>
        {children}
      </div>
    </TabsContext.Provider>
  );
}

// Tab list container
export function TabList({ children, className, 'aria-label': ariaLabel }: TabListProps) {
  const { variant } = useTabsContext();
  const styles = variantStyles[variant];

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={clsx('flex', styles.list, className)}
    >
      {children}
    </div>
  );
}

// Individual tab button
export function Tab({ id, children, disabled = false, icon, badge, className }: TabProps) {
  const { activeTab, setActiveTab, variant, size } = useTabsContext();
  const isActive = activeTab === id;
  const styles = variantStyles[variant];
  const tabRef = useRef<HTMLButtonElement>(null);

  const handleClick = () => {
    if (!disabled) {
      setActiveTab(id);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <button
      ref={tabRef}
      role="tab"
      id={`tab-${id}`}
      aria-selected={isActive}
      aria-controls={`panel-${id}`}
      tabIndex={isActive ? 0 : -1}
      disabled={disabled}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={clsx(
        'flex items-center gap-2 font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
        sizeStyles[size],
        styles.tab.base,
        isActive ? styles.tab.active : styles.tab.inactive,
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      <span>{children}</span>
      {badge && <span className="ml-1">{badge}</span>}
    </button>
  );
}

// Tab panel content
export function TabPanel({ id, children, className }: TabPanelProps) {
  const { activeTab } = useTabsContext();
  const isActive = activeTab === id;

  if (!isActive) {
    return null;
  }

  return (
    <div
      role="tabpanel"
      id={`panel-${id}`}
      aria-labelledby={`tab-${id}`}
      tabIndex={0}
      className={clsx('focus:outline-none', className)}
    >
      {children}
    </div>
  );
}

// Simple tabs component for quick usage
export interface SimpleTabsProps {
  tabs: TabItem[];
  defaultTab?: string;
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  variant?: 'default' | 'pills' | 'underline';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  tabListClassName?: string;
  fullWidth?: boolean;
  children?: (activeTab: string) => React.ReactNode;
}

export function SimpleTabs({
  tabs,
  defaultTab,
  activeTab,
  onTabChange,
  variant = 'default',
  size = 'md',
  className,
  tabListClassName,
  fullWidth = false,
  children,
}: SimpleTabsProps) {
  const [internalActiveTab, setInternalActiveTab] = useState(defaultTab || tabs[0]?.id || '');
  const isControlled = activeTab !== undefined;
  const currentTab = isControlled ? activeTab : internalActiveTab;

  const handleTabChange = (tabId: string) => {
    if (!isControlled) {
      setInternalActiveTab(tabId);
    }
    onTabChange?.(tabId);
  };

  return (
    <Tabs
      activeTab={currentTab}
      onTabChange={handleTabChange}
      variant={variant}
      size={size}
      className={className}
      fullWidth={fullWidth}
    >
      <TabList className={tabListClassName}>
        {tabs.map((tab) => (
          <Tab
            key={tab.id}
            id={tab.id}
            disabled={tab.disabled}
            icon={tab.icon}
            badge={tab.badge}
          >
            {tab.label}
          </Tab>
        ))}
      </TabList>
      {children && (
        <div className="mt-4">
          {children(currentTab)}
        </div>
      )}
    </Tabs>
  );
}
